/**
 * fr-FR display formatters. Date inputs are ISO-8601 UTC strings from the API;
 * they are rendered in UTC so the displayed calendar day is stable regardless
 * of the viewer's timezone.
 */
const PLACEHOLDER = '—'

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
})

const dateShortFmt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  timeZone: 'UTC',
})

const dateTimeFmt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
})

const numberFmt = new Intl.NumberFormat('fr-FR')

const relativeFmt = new Intl.RelativeTimeFormat('fr-FR', { numeric: 'auto' })

const rateFmt = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const moneyFmt = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
})

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDate(iso: string | null | undefined): string {
  const date = parse(iso)
  return date ? dateFmt.format(date) : PLACEHOLDER
}

/** Compact day/month for dense axis labels (e.g. a timeseries chart). */
export function formatDateShort(iso: string | null | undefined): string {
  const date = parse(iso)
  return date ? dateShortFmt.format(date) : PLACEHOLDER
}

export function formatDateTime(iso: string | null | undefined): string {
  const date = parse(iso)
  return date ? dateTimeFmt.format(date) : PLACEHOLDER
}

/**
 * Freshness label for an auto-refreshing view: "à l'instant" under a minute,
 * then "il y a N minutes" / "il y a N heures". `now` is injected (rather than
 * read from Date.now() here) so the caller can drive re-rendering from a ticking
 * clock and keep the label current between data fetches.
 */
export function formatRelativeTime(from: number, now: number): string {
  const minutes = Math.floor((now - from) / 60_000)
  if (minutes < 1) return "à l'instant"
  if (minutes < 60) return relativeFmt.format(-minutes, 'minute')
  return relativeFmt.format(-Math.floor(minutes / 60), 'hour')
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return PLACEHOLDER
  return numberFmt.format(value)
}

/**
 * Commission rate. The API contract only says "decimal, ≥ 0" without fixing the
 * scale, so we do NOT rescale (no ×100): the value is shown verbatim with a "%"
 * suffix (e.g. 15 → "15 %", 7.5 → "7,5 %"). If the backend later confirms a
 * fractional scale (0.15), switch to Intl percent style here in one place.
 */
export function formatCommission(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return PLACEHOLDER
  return `${rateFmt.format(value)} %`
}

/**
 * Money amount in euros (the API sends earnings as a decimal number of euros,
 * e.g. 12.5 → "12,50 €"). Renders the placeholder for missing/NaN values.
 */
export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return PLACEHOLDER
  return moneyFmt.format(value)
}
